import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Badge } from 'react-bootstrap';

const SupportTicketManager = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyMessage, setReplyMessage] = useState('');
    const chatEndRef = useRef(null);

    const [newTicket, setNewTicket] = useState({
        subject: '',
        priority: 'Medium',
        message: ''
    });

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const response = await api.get('/clinic/support-tickets');
            setTickets(response.data);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post('/clinic/support-tickets', newTicket);
            Swal.fire('Success', 'Support ticket created successfully', 'success');
            setShowCreateModal(false);
            setNewTicket({ subject: '', priority: 'Medium', message: '' });
            fetchTickets();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Failed to create ticket', 'error');
        }
    };

    const handleCloseTicket = async () => {
        try {
            const result = await Swal.fire({
                title: 'Close this ticket?',
                text: "You won't be able to reply until it's reactivated.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, close it!'
            });

            if (result.isConfirmed) {
                const response = await api.put(`/clinic/support-tickets/${selectedTicket._id}/close`, {});

                setSelectedTicket(response.data);
                fetchTickets();
                Swal.fire('Closed!', 'Ticket has been closed.', 'success');
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to close ticket', 'error');
        }
    };

    const handleReactivateTicket = async () => {
        try {
            const response = await api.put(`/clinic/support-tickets/${selectedTicket._id}/reactivate`, {});

            setSelectedTicket(response.data);
            fetchTickets();
            Swal.fire('Reactivated!', 'Ticket is now Open.', 'success');
        } catch (error) {
            Swal.fire('Error', 'Failed to reactivate ticket', 'error');
        }
    };

    const handleReplySubmit = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim()) return;

        try {
            const response = await api.post(`/clinic/support-tickets/${selectedTicket._id}/reply`, { message: replyMessage });

            // Update local state with populated response
            setSelectedTicket(response.data);
            setReplyMessage('');

            // Refresh list in background to update status/timestamps
            fetchTickets();

            // Auto scroll to bottom
            setTimeout(() => {
                if (chatEndRef.current) {
                    chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);

        } catch (error) {
            Swal.fire('Error', 'Failed to send reply', 'error');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Open': return 'bg-success-focus text-success-600';
            case 'In Progress': return 'bg-warning-focus text-warning-600';
            case 'Resolved': return 'bg-info-focus text-info-600';
            case 'Closed': return 'bg-secondary text-white';
            default: return 'bg-light text-dark';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'text-danger fw-bold';
            case 'Medium': return 'text-warning fw-bold';
            case 'Low': return 'text-success fw-bold';
            default: return 'text-secondary';
        }
    };

    const openDetailModal = (ticket) => {
        setSelectedTicket(ticket);
        setShowDetailModal(true);
    };

    return (
        <div className="card h-100 p-0 radius-12 fade-in-up">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    <div className="w-40-px h-40-px radius-8 bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-xl">
                        <Icon icon="solar:help-bold" />
                    </div>
                    <div>
                        <h5 className="mb-0 fw-bold text-dark">Support & Help</h5>
                        <p className="text-sm text-secondary mb-0">Manage your support requests and inquiries.</p>
                    </div>
                </div>
                <button
                    className="btn btn-primary px-20 py-10 radius-8 d-flex align-items-center gap-2 shadow-sm hover-scale"
                    onClick={() => setShowCreateModal(true)}
                >
                    <Icon icon="solar:pen-new-square-bold" className="text-xl" />
                    Create New Ticket
                </button>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive">
                    <table className="table bordered-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">Ticket ID</th>
                                <th scope="col">Subject</th>
                                <th scope="col">Priority</th>
                                <th scope="col">Status</th>
                                <th scope="col">Last Updated</th>
                                <th scope="col" className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                            ) : tickets.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-5 text-secondary">No support tickets found. Create one to get started.</td></tr>
                            ) : (
                                tickets.map(ticket => (
                                    <tr key={ticket._id} className="hover-bg-light transition-all">
                                        <td><span className="fw-medium text-secondary">#{ticket._id.substring(ticket._id.length - 6).toUpperCase()}</span></td>
                                        <td>
                                            <span className="d-block fw-semibold text-dark">{ticket.subject}</span>
                                            <span className="text-xs text-secondary text-truncate d-inline-block" style={{ maxWidth: '250px' }}>{ticket.message}</span>
                                        </td>
                                        <td><span className={getPriorityColor(ticket.priority)}>{ticket.priority}</span></td>
                                        <td>
                                            <span className={`px-10 py-4 radius-4 text-xs fw-bold ${getStatusColor(ticket.status)}`}>
                                                {ticket.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="text-secondary text-sm">
                                            {new Date(ticket.updatedAt || ticket.createdAt).toLocaleDateString()}
                                            <br />
                                            <small>{new Date(ticket.updatedAt || ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                className="btn btn-outline-primary btn-sm px-12 radius-6 fw-medium"
                                                onClick={() => openDetailModal(ticket)}
                                            >
                                                View & Reply
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* CREATE TICKET MODAL */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Create Support Ticket</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Subject</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Briefly describe the issue..."
                                value={newTicket.subject}
                                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                required
                                className="shadow-none border-secondary-light"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Priority</Form.Label>
                            <Form.Select
                                value={newTicket.priority}
                                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                                className="shadow-none border-secondary-light"
                            >
                                <option value="Low">Low - General Inquiry</option>
                                <option value="Medium">Medium - Feature Issue/Bug</option>
                                <option value="High">High - Critical System Failure</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold">Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="Provide detailed information about your issue..."
                                value={newTicket.message}
                                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                                required
                                className="shadow-none border-secondary-light"
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                            <Button variant="primary" type="submit" className="px-4">Submit Ticket</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* TICKET DETAILS MODAL */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg" className="modal-custom">
                <Modal.Body className="p-0">
                    {selectedTicket && (
                        <div className="d-flex flex-column h-100">
                            {/* Header */}
                            <div className="p-24 border-bottom bg-light d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <h5 className="mb-0 fw-bold">{selectedTicket.subject}</h5>
                                        <span className={`px-8 py-2 radius-4 text-xs fw-bold ${getStatusColor(selectedTicket.status)}`}>
                                            {selectedTicket.status}
                                        </span>
                                    </div>
                                    <div className="d-flex gap-3 text-sm text-secondary">
                                        <span><Icon icon="solar:calendar-bold" className="me-1" /> {new Date(selectedTicket.createdAt).toLocaleString()}</span>
                                        <span className={getPriorityColor(selectedTicket.priority)}>{selectedTicket.priority} Priority</span>
                                        <span>Ref: #{selectedTicket._id.substring(selectedTicket._id.length - 6).toUpperCase()}</span>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    {selectedTicket.status !== 'Closed' && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={handleCloseTicket}
                                            className="d-flex align-items-center gap-1 radius-8 px-3"
                                        >
                                            <Icon icon="solar:close-circle-bold" /> Close
                                        </Button>
                                    )}

                                    {selectedTicket.status === 'Resolved' && (
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleReactivateTicket}
                                            className="d-flex align-items-center gap-1 radius-8 px-3 text-white"
                                        >
                                            <Icon icon="solar:restart-bold" /> Reactivate
                                        </Button>
                                    )}
                                    <button type="button" className="btn-close ms-2" onClick={() => setShowDetailModal(false)}></button>
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="p-4 bg-light" style={{ height: '400px', overflowY: 'auto', background: '#f8fafc' }}>
                                <div className="d-flex flex-column gap-3">
                                    {/* Initial Message */}
                                    <div className="d-flex justify-content-start">
                                        <div className="radius-12 p-3 bg-white shadow-sm border" style={{ maxWidth: '80%' }}>
                                            <div className="d-flex justify-content-between align-items-center gap-4 mb-2">
                                                <span className="text-xs fw-bold px-2 py-1 rounded bg-primary-50 text-primary-600">
                                                    You (Original Request)
                                                </span>
                                                <span className="text-xs text-secondary">
                                                    {new Date(selectedTicket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                            <p className="mb-0 text-sm" style={{ lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{selectedTicket.message}</p>
                                        </div>
                                    </div>

                                    {/* Responses */}
                                    {selectedTicket.responses && selectedTicket.responses.map((res, idx) => {
                                        const currentUserId = JSON.parse(atob(localStorage.getItem('token').split('.')[1])).id;
                                        const isMyMessage = res.responder === currentUserId || res.responder?._id === currentUserId;

                                        return (
                                            <div key={idx} className={`d-flex ${isMyMessage ? 'justify-content-start' : 'justify-content-end'}`}>
                                                <div className={`radius-12 p-3 ${isMyMessage ? 'bg-white shadow-sm border' : 'bg-primary text-white shadow-sm'}`} style={{ maxWidth: '80%' }}>
                                                    <div className="d-flex justify-content-between align-items-center gap-4 mb-2">
                                                        <span className={`text-xs fw-bold px-2 py-1 rounded ${isMyMessage ? 'bg-primary-50 text-primary-600' : 'bg-white text-primary'}`}>
                                                            {isMyMessage ? 'You' : (res.responder?.name || 'Support Admin')}
                                                        </span>
                                                        <span className={`text-xs ${isMyMessage ? 'text-secondary' : 'text-white-50'}`}>
                                                            {new Date(res.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <p className="mb-0 text-sm" style={{ lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{res.message}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={chatEndRef} />
                                </div>
                            </div>

                            {/* Reply Box */}
                            <div className="p-24 border-top bg-light">
                                {selectedTicket.status === 'Closed' || selectedTicket.status === 'Resolved' ? (
                                    <div className={`text-center py-2 radius-8 border fw-bold text-sm ${selectedTicket.status === 'Closed' ? 'bg-warning-50 text-warning-700 border-warning-200' : 'bg-success-50 text-success-700 border-success-200'}`}>
                                        <Icon icon={selectedTicket.status === 'Closed' ? "solar:shield-warning-bold" : "solar:check-circle-bold"} className="me-2 text-lg align-middle" />
                                        This ticket is {selectedTicket.status.toLowerCase()}. You can only view the conversation.
                                    </div>
                                ) : (
                                    <Form onSubmit={handleReplySubmit}>
                                        <div className="d-flex gap-2">
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                placeholder="Type your reply here..."
                                                value={replyMessage}
                                                onChange={(e) => setReplyMessage(e.target.value)}
                                                className="radius-8 shadow-sm border-0"
                                                required
                                            />
                                            <Button type="submit" variant="primary" className="px-4 radius-8 fw-semibold d-flex flex-column justify-content-center align-items-center gap-1">
                                                <Icon icon="solar:plain-bold" className="text-xl" />
                                                Send
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div >
    );
};

export default SupportTicketManager;
